import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import { Row, Nav, Tab } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { getFromLS, setToLS } from "../../utils/storage.js";
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { logout, getDeepLink } from "utils/index.js";

// Dashboard Pills
import DiffToolMainClientGridPage from "../client_reconciliation/diff_tool_main_client_grid_page/DiffToolMainClientGridPage.js";
import MyClients from "../client_onboarding/MyClients.js";
import ClientsGrid from "../../pages/client_onboarding/ClientsGrid/ClientsGrid";
import ClientApprovalGrid from "../../pages/client_onboarding/ClientApprovalGrid/ClientApprovalGrid";
import ClientDataMapping from "../../pages/client_onboarding/ClientDataMapping/ClientDataMapping";
// Intelligence report Pills
import { ConfirmationModal } from "./ConfirmationModal.js";
import EL_Dashboard from "../engagement_letter/dashboard/dashboard.js";
import EngagementDashboard from "../../pages/cygnus_dashboards/Engagement_Dashboard/EngagementDashboard";
import EngagementDashboardOld from "../../pages/cygnus_dashboards/Engagement_Dashboard_Old/EngagementDashboardOld";
import ClientDashboard from "../../pages/cygnus_dashboards/Client_Dashboard/ClientDashboard";
import MyTemplatesPage from "../engagement_letter/my_templates_page/MyTemplatesPage.js";
// import EngagementLetterDetailsPage from "../engagement_letter/engagement_letter_details_page/EngagementLetterDetailsPage.js";
import ApprovalDetailsPage from "../../pages/engagement_letter/ApprovalDetailsPage/ApprovalDetailsPage";

import ContentBlocksPage from "../engagement_letter/ContentBlocksPage/ContentBlocksPage";
import RulesPage from "../engagement_letter/RulesPage/RulesPage";
import WizardFormConfig from "../engagement_letter/wizard_form_builder/my_wizard_forms/WizardFormConfig.js";
import ApprovalConfig from "../firm_setup/ApprovalConfig/ApprovalConfig";
import OnboardingFormConfig from "../../pages/client_onboarding/OnboardingFormConfig/OnboardingFormConfig";
import IntakeFormsGrid from "../../pages/client_onboarding/IntakeFormsGrid/IntakeFormsGrid"
import EngagementFormsGrid from "../../pages/engagement_letter/EngagementFormsGrid/EngagementFormsGrid";
import EngagementFormConfig from "../../pages/engagement_letter/EngagementFormConfig/EngagementFormConfig";
import MDMFieldsConfig from "../../pages/client_onboarding/MDMFieldsConfig/MDMFieldsConfig";
import FieldMappings from "../../pages/firm_setup/FieldMappings/FieldMappings";
import EngagementLetterDetailsPage from "../../pages/engagement_letter/EngagementLetterDetailsPage/EngagementLetterDetailsPage";
// import FlowRunCountDashboard from "../reports_dashboard/reports/FlowRunCountDashboard.js";
import CygnusGptDashboard from "../../pages/cygnus_gpt/CygnusGptDashboard/CygnusGptDashboard";

import { useTheme } from "../../themes/useTheme";
import { useChartTheme } from "../../themes/useChartTheme";
import chart_themes from "../../themes/chartThemes.json";
import { applyCssVars } from "../../themes/useTheme";

const Dashboard = () => {
  // User details 
  const firm_img = localStorage.getItem("firmImg")
  const userType = localStorage.getItem("usertype");
  const firmname = localStorage.getItem("firmname")
  const availableFeatures = getFromLS("active-features")
  const navigate = useNavigate()
  const location = useLocation()
  const [deepLink, setDeepLink] = useState(undefined);

  const { setMode } = useTheme();
  const { setChartMode } = useChartTheme();
  // Match main Dashboard.tsx: fall back to Clients when no default tab is stored.
  const DEFAULT_ACTIVE_KEY = "clients";

const saveParamToApi = async (parameter_name, value) => {
  const firm_id = localStorage.getItem("firmId");
  if (!firm_id || !value) return;
  try {
    await fetch(`${process.env.REACT_APP_BASE_URI}/workflow/personalization/set_params/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firm_id,
        parameter_name,
        parameter_value_1: JSON.stringify(value),
      }),
    });
  } catch (err) {
    console.error(`Failed to save ${parameter_name}:`, err);
  }
};
  // Hydrate theme values into local state only. Main does not write these back
  // during Dashboard mount; persisting here creates unrelated set_params calls.
  const broadcastThemeUpdates = ({ catalog, chartCatalog, active, activeChart }) => {
  if (catalog && Object.keys(catalog).length) {
    setToLS("all-themes", catalog);
  }
  if (chartCatalog && Object.keys(chartCatalog).length) setToLS("chart-themes", chartCatalog);

  if (active) {
    setToLS("theme", active);
    setMode(active);
    applyCssVars(active);
  }
  if (activeChart) {
    setToLS("chart-theme", activeChart);
    setChartMode(activeChart);
  }

  window.dispatchEvent(new CustomEvent("cygnus:themes:catalog", { detail: { catalog, chartCatalog } }));
  window.dispatchEvent(new CustomEvent("cygnus:themes:active", { detail: { theme: active, chartTheme: activeChart } }));
};

  useEffect(() => {
    const firm_id = localStorage.getItem("firmId");
    if (!firm_id) return;

    const fetchParam = async (parameter_name) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URI}/workflow/personalization/get_params/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ firm_id, parameter_name }),
          }
        );
        const json = await res.json();
        const row = json?.data?.[0]?.params?.[0]?.parameter_value_1;
        return row ? JSON.parse(row) : null;
      } catch (err) {
        console.error("Theme fetch failed:", err);
        return null;
      }
    };

    (async () => {
      // pull everything in parallel
      const [active, activeChart, allThemes] = await Promise.all([
        fetchParam("activeTheme"),
        fetchParam("activeChartTheme"),
        fetchParam("themes"),
      ]);

      // fallbacks for chart themes catalog (you ship these)
      const chartCatalog = chart_themes?.data || {};

      broadcastThemeUpdates({
        catalog: allThemes || {},          // UI theme catalog (may be null)
        chartCatalog,                      // chart theme catalog (bundled fallback)
        active,                            // active UI theme (may be null)
        activeChart,                       // active chart theme (may be null)
      });
    })();
  }, []); // mount once

  useEffect(() => {
    const dl = getDeepLink();
    if (!dl) return;

    // navigate to the tab/pill if provided (defaults allowed)
    if (dl.tab && RENDER_MAP[dl.tab]) {
      localStorage.setItem("defaultActiveKey", dl.tab);
      setActiveKey(dl.tab);
      // Re-render the map entry with the deepLink injected
      if (dl.tab === "client_approval") {
        setRender(CustomClientApprovalRender(dl));
      }
      else if (dl.tab === "approval_details") {
        setRender(CustomELApprovalRender(dl));
      } else {
        // Normal pages should render from activeKey, not from a cached element.
        setRender(null);
      }
    }

    // 2) stash for children that care (approval grid)
    setDeepLink(dl);
  }, []); // run once on load


  // Sidenav open close feature
  let [isOpen, setIsOpen] = useState(false);
  let [forcedOpen, setForcedOpen] = useState(false);
  let [logoutIcon, setLogoutIcon] = useState(false)
  const openNav = () => {
    document.getElementById("mySidenav").style.width = "250px";
    document.getElementById("dashboard-content").style.marginLeft = "250px";
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  };
  const closeNav = () => {
    document.getElementById("mySidenav").style.width = "70px";
    document.getElementById("dashboard-content").style.marginLeft = "70px";
    setIsOpen(false);
  };

  const sidenavOpenDoor = () => {
    setLogoutIcon(true)
  }
  const sidenavCloseDoor = () => {
    setLogoutIcon(false)
  }

  const redirectToDatahub = () => {
    const currentUrl = window.location.href.toLowerCase();

    let url = null;
    let environment = "prod";

    const isLocal =
      currentUrl.includes("localhost") ||
      currentUrl.includes("127.0.0.1") ||
      currentUrl.includes("0.0.0.0") ||
      currentUrl.includes("[::1]");

    if (currentUrl.includes("azurewebsites") || isLocal) {
      environment = "dev";
      url = process.env.REACT_APP_DATAHUB_DEV_URL;
    } else {
      environment = "prod";
      url = process.env.REACT_APP_DATAHUB_PROD_URL;
    }

    console.log(`this is URL - ${url} and this is environment - ${environment}`);

    if (url) {
      window.open(url, "_blank");
    } else {
      console.error("invalid environment configuration");
    }
  };

  // Setting default opening pill based on user (this is hard coded right now)
  // Done in Login.js

  // Navigate with props to bring in dynamic behaviour
  /**
   * 
   * @param {*} pill : This arguement tells us where to navigate, there is no default value.
   * @param {*} mapping_option : This arguement is by default null and is only used when navigating to mapping page.
   * @param {*} report_navigator : This is a boolean arguement which tells us if navigation is within Intelligence
   *                               report and we then do not need to update dashboard nav-pills
   */
  const navigateWithData = (pill, options = { mapping_option: null, onboarding_form_config_option: null, engagement_form_config_option: null, report_navigator: false }) => {
    const mapping_option = options.mapping_option;
    const onboarding_form_config_option = options.onboarding_form_config_option;
    const engagement_form_config_option = options.engagement_form_config_option;
    const report_navigator = options.report_navigator;
    if (pill != undefined && pill.length != 0) {
      if (report_navigator) {
        // here we only navigate to report but do not change the active dashboard pill
        setRender(RENDER_MAP[pill]);
      }
      else if (mapping_option != null || mapping_option != undefined) {
        // here we navigate to mapping with a mapping_option for pre-selected dropdown
        // change the active dashboard pill to mapping
        localStorage.setItem("defaultActiveKey", pill);
        setActiveKey(pill);
        setRender(CustomClientDataMappingRender(mapping_option));
      }
      else if (onboarding_form_config_option != null || onboarding_form_config_option != undefined) {
        localStorage.setItem("defaultActiveKey", pill);
        setActiveKey(pill)
        setRender(CustomOnboardingFormConfigRender(onboarding_form_config_option))
      }
      else if (engagement_form_config_option != null || engagement_form_config_option != undefined) {
        localStorage.setItem("defaultActiveKey", pill);
        setActiveKey(pill)
        setRender(CustomEngagementFormConfigRender(engagement_form_config_option))
      }
      else {
        // here we navigate to pill and change the active dashboard pill to selected Pill
        if (["dynamicform"].includes(activeKey)) {
          setConfirmationModal(true)
          setNavigationData(pill)
        } else {
          localStorage.setItem("defaultActiveKey", pill);
          setActiveKey(pill);
          // Main Dashboard.tsx derives normal pages from activeKey on each render.
          // Clearing render prevents stale page instances, especially KPI payload pages.
          setRender(null);
          navigate(`/dashboard?tab=${pill}`);
        }
      }

    } else if (pill != "empty") {
      localStorage.setItem("defaultActiveKey", pill);
      setActiveKey(pill);
      setRender(null);
    } else {
      console.log("NO PAGE FOUND TO NAVIGATE.")
    }
  };

  const CustomClientDataMappingRender = (mapping_option) => {
    return (
      <ClientDataMapping mapoption={mapping_option} transporter={navigateWithData} />
    )
  }

  const CustomClientApprovalRender = (deepLink) => {
    return (
      <ClientApprovalGrid
        transporter={navigateWithData}
        deepLink={deepLink}
      />
    );
  };

  const CustomELApprovalRender = (deepLink) => {
    return (
      <ApprovalDetailsPage
        transporter={navigateWithData}
        deepLink={deepLink}
      />
    );
  };

  const CustomOnboardingFormConfigRender = (onboarding_form_config_option) => {
    console.log("IS THIS CUSTOM FUNCTION BEING CALLED?", onboarding_form_config_option)
    return (
      <OnboardingFormConfig onboarding_form_config_option={onboarding_form_config_option} transporter={navigateWithData} />
    )
  }

  const CustomEngagementFormConfigRender = (engagement_form_config_option) => {
    console.log("IS THIS CUSTOM FUNCTION BEING CALLED?", engagement_form_config_option)
    return (
      <EngagementFormConfig engagement_form_config_option={engagement_form_config_option} transporter={navigateWithData} />
    )
  }

  const CustomWizardFormConfigRender = (wizard_config_option) => {
    console.log("IS THIS CUSTOM FUNCTION BEING CALLED?", wizard_config_option)
    return (
      <WizardFormConfig configoption={wizard_config_option} transporter={navigateWithData} />
    )
  }

  // const CustomMaintenanceReportRender = (report_option) => {
  //   // console.log("IS THIS CUSTOM FUNCTION BEING CALLED?", report_option)
  //   return (
  //     <MaintanenceReports reportoption={report_option} transporter={navigateWithData} />
  //   )
  // }

  /**
   *  Global mapper for page navigation
   *                                !!!!! WARNING !!!!
   *  Please do not change any of the existing keys in RENDER_MAP as they are being used in different places. 
   *                       Doing so may end up breaking the functionality.
   */
  const RENDER_MAP = {
    // Admins
    // superadmin: <SuperAdmin transporter={navigateWithData} />,
    // firmadmin: <FirmAdmin transporter={navigateWithData} />,
    // Dashboard pills: Page component

    // dashboard: <ReportDashboard transporter={navigateWithData} />,
    onboarding_forms: <IntakeFormsGrid transporter={navigateWithData} />,
    field_mapping: <CustomClientDataMappingRender />,
    configure_fields: <MDMFieldsConfig transporter={navigateWithData} />,
    engagement_forms: <EngagementFormsGrid transporter={navigateWithData} />,
    onboarding_form_editor: <CustomOnboardingFormConfigRender />,
    engagement_form_editor: <CustomEngagementFormConfigRender />,
    // client_onboarding_form_config: <CustomClientOnboardingFormConfigRender />,
    logs: <ClientDataMapping transporter={navigateWithData} />,
    client_reconciliation: <DiffToolMainClientGridPage transporter={navigateWithData} />,
    client_approval: <CustomClientApprovalRender />,
    mappings: <FieldMappings transporter={navigateWithData} />,
    approval_config: <ApprovalConfig transporter={navigateWithData} />,
    // clients: <MyClients transporter={navigateWithData} />,
    el_dashboard: (<EngagementDashboard transporter={navigateWithData} saveParamToApi={saveParamToApi}/>),
    el_dashboard_old: (<EngagementDashboardOld transporter={navigateWithData} />),
    client_dashboard: (<ClientDashboard transporter={navigateWithData} saveParamToApi={saveParamToApi} />),
    clients: <ClientsGrid transporter={navigateWithData} />,
    cygnus_gpt_dashboard: <CygnusGptDashboard transporter={navigateWithData} />,
    templates: <MyTemplatesPage transporter={navigateWithData} />,
    // EL Dashboard KPI navigation changes only the query string. Include it in
    // the key so EngagementLetterDetailsPage remounts and reads the current payload.
    client_details: (
      <EngagementLetterDetailsPage
        key={location.search}
        transporter={navigateWithData}
      />
    ),
    approval_details: <CustomELApprovalRender />,
    // wizard_form_builder: <MyWizardForms transporter={navigateWithData} />,
    // template_mappings: <TemplateMappingsPage transporter={navigateWithData} />,
    content_blocks: <ContentBlocksPage transporter={navigateWithData} />,
    rules: <RulesPage transporter={navigateWithData} />,

    // INNTERNAL REPORTS NAVIGATOR
    // ZipCodeMap: <GeoDistMap transporter={navigateWithData} />,
    // SingleVsJointFiling: <SingleVsJointFiling transporter={navigateWithData} />,
    // IndividualVsEntityChart: <IndividualVsEntityChart transporter={navigateWithData} />,
    // ExtensionStatus: <ExtensionStatus transporter={navigateWithData} />,
    // EntityTypeDistribution: <EntityTypeDistribution transporter={navigateWithData} />,
    // TopCustomers: <TopCustomers transporter={navigateWithData} />,
    // FlowRunCountDashboard: <FlowRunCountDashboard transporter={navigateWithData} />,

  };

  // Keep render only for special custom/deeplink renders. Normal pages come from
  // activeKey below, matching main Dashboard.tsx and avoiding stale page elements.
  const [render, setRender] = useState(null);
  const [activeKey, setActiveKey] = useState(localStorage.getItem("defaultActiveKey") || DEFAULT_ACTIVE_KEY);
  const [openSetup, setOpenSetup] = useState(true);
  const [openEngagementLetterSetup, setOpenEngagementLetterSetup] = useState(true);
  const [openEngagementLetterSetting, setOpenEngagementLetterSetting] = useState(true);
  const [openApproval, setOpenApproval] = useState(true);
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [navigationData, setNavigationData] = useState({})
  const [openDashboard, setOpenDashboard] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");

    if (tabFromUrl && RENDER_MAP[tabFromUrl]) {
      setActiveKey(tabFromUrl);
      localStorage.setItem("defaultActiveKey", tabFromUrl);
      // Route/query changes should recreate the normal page from RENDER_MAP.
      setRender(null);
    }
  }, [location.search]);

  const continueNavigation = (navigationData) => {
    localStorage.setItem("defaultActiveKey", navigationData);
    setActiveKey(navigationData);
    setRender(null);
  }

  // Main-style rendering: derive the displayed page from activeKey each render.
  // render is only an override for custom option/deeplink pages.
  const currentRender = render || RENDER_MAP[activeKey];

  const SetupToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setOpenSetup(!openSetup)
    );

    return (
      <Nav.Link eventKey="" onClick={decoratedOnClick}>
        <Icon.GearWideConnected />
        {isOpen ? <p>&nbsp;Firm&nbsp;Setup {children}
          {openSetup
            ?
            <Icon.ChevronRight className="chevy" />
            :
            <Icon.ChevronDown className="chevy" />
          }
        </p> : ""}

      </Nav.Link>
    );
  }

  const EngagementLetterSetupToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setOpenEngagementLetterSetup(!openEngagementLetterSetup)
    );

    return (
      <Nav.Link eventKey="" onClick={decoratedOnClick}>
        <Icon.GearWideConnected />
        {isOpen ? <p>&nbsp;EL&nbsp;Setup {children}
          {openEngagementLetterSetup
            ?
            <Icon.ChevronRight className="chevy" />
            :
            <Icon.ChevronDown className="chevy" />
          }
        </p> : ""}

      </Nav.Link>
    );
  }

  const EngagementLetterSettingToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setOpenEngagementLetterSetting(!openEngagementLetterSetting)
    );

    return (
      <Nav.Link eventKey="" onClick={decoratedOnClick}>
        <Icon.GearFill />
        {isOpen ? <p>&nbsp;Setting {children}
          {openEngagementLetterSetting
            ?
            <Icon.ChevronRight className="chevy" />
            :
            <Icon.ChevronDown className="chevy" />
          }
        </p> : ""}

      </Nav.Link>
    );
  }

  const ApprovalToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setOpenApproval(!openApproval)
    );

    return (
      <Nav.Link eventKey="" onClick={decoratedOnClick}>
        <Icon.ClipboardCheckFill />
        {isOpen ? <p>&nbsp;Approval&nbsp;{children}
          {openSetup
            ?
            <Icon.ChevronRight className="chevy" />
            :
            <Icon.ChevronDown className="chevy" />
          }
        </p> : ""}

      </Nav.Link>
    );
  }

  const DashboardToggle = ({ children, eventKey }) => {
    const decoratedOnClick = useAccordionButton(eventKey, () =>
      setOpenDashboard(!openDashboard)
    );

    return (
      <Nav.Link eventKey="" onClick={decoratedOnClick}>
        <Icon.Grid1x2Fill />
        {isOpen ? (
          <p>&nbsp;Dashboard {children}
            {openDashboard
              ? <Icon.ChevronRight className="chevy" />
              : <Icon.ChevronDown className="chevy" />
            }
          </p>
        ) : ""}
      </Nav.Link>
    );
  };



  return (
    <>
      <div className="wrapper">
        <div
          id="mySidenav"
          className={`sidenav ${isOpen ? "is-open" : ""}`}   // ⬅️ add is-open
          onMouseEnter={() => openNav()}
          onMouseLeave={() => forcedOpen ? console.log() : closeNav()}
        >
          <ConfirmationModal
            show={confirmationModal}
            onHide={() => setConfirmationModal(false)}
            navigationdata={navigationData}
            stayandsave={() => setConfirmationModal(false)}
            skipandleave={(navigationData) => {
              setConfirmationModal(false)
              continueNavigation(navigationData)
            }
            }
          />
          {
            <div className="flex-row-center-start go-home" onClick={() => navigate("/dashboard")}>
              <img className="sidenav-dashboard-logo" src={require("../../images/dashboard_logo.png")} />
              <p className="typing-text">{isOpen ? <>&nbsp;Cygnus&nbsp;Accounting&nbsp;Hub</> : ""}</p>
              <div className={forcedOpen ? isOpen ? "sidenav-open" : "sidenav-lock" : "sidenav-lock"}>
                {
                  forcedOpen ? <Icon.LockFill onClick={() => setForcedOpen(false)} /> : <Icon.UnlockFill onClick={() => setForcedOpen(true)} />
                }
              </div>
            </div>
          }
          <hr className="sidenav-divider" />
          <Tab.Container
            id="left-tabs"
            activeKey={activeKey}
            defaultActiveKey={activeKey}
          >
            <Nav variant="pills" defaultActiveKey={activeKey} className="sidenav-scroll">
              {
                ((availableFeatures.filter(str => str.includes("AI_COMP"))).length > 0) &&
                <Nav.Item onClick={() => navigateWithData("cygnus_gpt_dashboard")}>
                  <Nav.Link eventKey="cygnus_gpt_dashboard">
                    <img className="ai-companion-logo" src={require("../../images/cygnus_ai.png")} />
                    {isOpen ? <p style={{ paddingLeft: 10 }}>&nbsp;AI&nbsp;Companion&nbsp;</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
              {
            (availableFeatures.includes("EL_DSH") || availableFeatures.includes("EL_DSH2") || availableFeatures.includes("CLIENT_DSH")) && (
              <Accordion className="sidenav-dropdown">
                <Nav.Item>
                  <DashboardToggle eventKey="10" />
                </Nav.Item>

                <Accordion.Collapse eventKey="10">
                  <>
                    {availableFeatures.includes("CLIENT_DSH") && (
                      <Nav.Item onClick={() => navigateWithData("client_dashboard")}>
                        <Nav.Link eventKey="client_dashboard">
                          <Icon.Grid1x2Fill />
                          {isOpen ? <p>&nbsp;Client Dashboard</p> : ""}
                        </Nav.Link>
                      </Nav.Item>
                    )}

                    {availableFeatures.includes("EL_DSH2") && (
                      <Nav.Item onClick={() => navigateWithData("el_dashboard")}>
                        <Nav.Link eventKey="el_dashboard">
                          <Icon.Grid1x2Fill />
                          {isOpen ? <p>&nbsp;EL Dashboard - V2</p> : ""}
                        </Nav.Link>
                      </Nav.Item>
                    )}

                    {availableFeatures.includes("EL_DSH") && (
                      <Nav.Item onClick={() => navigateWithData("el_dashboard_old")}>
                        <Nav.Link eventKey="el_dashboard_old">
                          <Icon.Grid1x2Fill />
                          {isOpen ? <p>&nbsp;EL Dashboard</p> : ""}
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </>
                </Accordion.Collapse>
              </Accordion>
              )
              }
              {
                ((availableFeatures.filter(str => str.includes("CLT_MAIN"))).length > 0) &&
                <Nav.Item onClick={() => navigateWithData("clients")}>
                  <Nav.Link eventKey="clients">
                    <Icon.FilePersonFill />
                    {isOpen ? <p>&nbsp;Clients&nbsp;</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
              {
                availableFeatures.includes("CLT_APRV") &&
                <Nav.Item onClick={() => navigateWithData("client_approval")}>
                  <Nav.Link eventKey="client_approval">
                    <Icon.PersonFillCheck />
                    {isOpen ? <p>&nbsp;Client&nbsp;Approval</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
              {availableFeatures.filter(str => str.includes("EL_CRT")).length > 0 &&
                // availableFeatures.includes("CGPT") &&
                <Nav.Item onClick={() => navigateWithData("client_details")}>
                  <Nav.Link eventKey="client_details">
                    <Icon.FileEarmarkRichtextFill />
                    {isOpen ? <p>&nbsp;Engagement&nbsp;Letters</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
              {
                availableFeatures.filter(str => str.includes("EL_APRV")).length > 0 &&
                <Nav.Item onClick={() => navigateWithData("approval_details")}>
                  <Nav.Link eventKey="approval_details">
                    <Icon.PersonFillCheck />
                    {isOpen ? <p>&nbsp;EL&nbsp;Approval</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
              {
                ((availableFeatures.filter(str => str.includes("EL_"))).length > 0) &&
                <Accordion className="sidenav-dropdown">
                  <Nav.Item>
                    <EngagementLetterSetupToggle eventKey="1"></EngagementLetterSetupToggle>
                  </Nav.Item>
                  <Accordion.Collapse eventKey="1">
                    <>
                      {
                        ((availableFeatures.filter(str => str.includes("EL_SET")).length > 0)
                        )
                        &&
                        <Accordion className="sidenav-dropdown-level-2">
                          <Nav.Item>
                            <EngagementLetterSettingToggle eventKey="2"></EngagementLetterSettingToggle>
                          </Nav.Item>
                          <Accordion.Collapse eventKey="2">
                            <>
                              {availableFeatures.filter(str => str.includes("EL_SET_TMPL")) &&
                                // availableFeatures.includes("CGPT") &&
                                <Nav.Item onClick={() => navigateWithData("templates")}>
                                  <Nav.Link eventKey="templates">
                                    <Icon.FileEarmarkRuledFill />
                                    {isOpen ? <p>&nbsp;Templates</p> : ""}
                                  </Nav.Link>
                                </Nav.Item>
                              }
                              {availableFeatures.filter(str => str.includes("EL_SET_WZFRM")) &&
                                // (availableFeatures.filter(str => str.includes("RPT_"))).length > 0 &&
                                <Nav.Item onClick={() => navigateWithData("engagement_forms")}>
                                  <Nav.Link eventKey="engagement_forms">
                                    <Icon.PostcardFill />
                                    {isOpen ? <p>&nbsp;Wizard&nbsp;Forms</p> : ""}
                                  </Nav.Link>
                                </Nav.Item>
                              }

                              {availableFeatures.filter(str => str.includes("EL_SET_CNTBLK")) &&
                                // availableFeatures.includes("CGPT") &&
                                <Nav.Item onClick={() => navigateWithData("content_blocks")}>
                                  <Nav.Link eventKey="content_blocks">
                                    <Icon.Paragraph />
                                    {isOpen ? <p>&nbsp;Content&nbsp;Blocks</p> : ""}
                                  </Nav.Link>
                                </Nav.Item>
                              }

                              {/* {availableFeatures.filter(str => str.includes("EL_SET_RLS")) && */}
                              <Nav.Item onClick={() => navigateWithData("rules")}>
                                <Nav.Link eventKey="rules">
                                  <Icon.Paragraph />
                                  {isOpen ? <p>&nbsp;Rules</p> : ""}
                                </Nav.Link>
                              </Nav.Item>
                              {/* } */}
                            </>
                          </Accordion.Collapse>
                        </Accordion>
                      }
                    </>
                  </Accordion.Collapse>
                </Accordion>
              }
              {
                ((availableFeatures.filter(str => str.includes("FRM_SETUP")).length > 0) ||
                  (availableFeatures.filter(str => str.includes("CFG_FLDS")).length > 0) ||
                  (availableFeatures.filter(str => str.includes("FLD_MAP")).length > 0) ||
                  (availableFeatures.filter(str => str.includes("INTK_TMPL")).length > 0) ||
                  (availableFeatures.filter(str => str.includes("APR_CFG")).length > 0) ||
                  (availableFeatures.filter(str => str.includes("SYS_MAP")).length > 0)
                ) &&
                <Accordion className="sidenav-dropdown">
                  <Nav.Item>
                    <SetupToggle eventKey="0"></SetupToggle>
                  </Nav.Item>
                  <Accordion.Collapse eventKey="0">
                    <>
                      {
                        availableFeatures.filter(str => str.includes("CFG_FLDS")).length > 0 &&
                        <Nav.Item onClick={() => navigateWithData("configure_fields")}>
                          <Nav.Link eventKey="configure_fields">
                            <Icon.Grid1x2Fill />
                            {isOpen ? <p>&nbsp;Configure&nbsp;Fields</p> : ""}
                          </Nav.Link>
                        </Nav.Item>
                      }
                      {
                        availableFeatures.filter(str => str.includes("FLD_MAP")).length > 0 &&
                        <Nav.Item onClick={() => navigateWithData("field_mapping")}>
                          <Nav.Link eventKey="field_mapping">
                            <Icon.Bezier />
                            {isOpen ? <p>&nbsp;Field&nbsp;Mapping</p> : ""}
                          </Nav.Link>
                        </Nav.Item>
                      }
                      {
                        availableFeatures.filter(str => str.includes("INTK_TMPL")).length > 0 &&
                        <Nav.Item onClick={() => navigateWithData("onboarding_forms")}>
                          <Nav.Link eventKey="onboarding_forms">
                            <Icon.PostcardFill />
                            {isOpen ? <p>&nbsp;Intake&nbsp;Templates</p> : ""}
                          </Nav.Link>
                        </Nav.Item>
                      }
                      {
                        availableFeatures.filter(str => str.includes("APR_CFG")).length > 0 &&
                        <Nav.Item onClick={() => navigateWithData("approval_config")}>
                          <Nav.Link eventKey="approval_config">
                            <Icon.GearFill />
                            {isOpen ? <p>&nbsp;Approval&nbsp;Configuration</p> : ""}
                          </Nav.Link>
                        </Nav.Item>
                      }
                      {
                        availableFeatures.filter(str => str.includes("SYS_MAP")).length > 0 &&
                        <Nav.Item onClick={() => navigateWithData("mappings")}>
                          <Nav.Link eventKey="mappings">
                            <Icon.GearFill />
                            {isOpen ? <p>&nbsp;Mappings</p> : ""}
                          </Nav.Link>
                        </Nav.Item>
                      }
                    </>
                  </Accordion.Collapse>
                </Accordion>
              }

              {userType === "Firm Admin" &&
                <Nav.Item onClick={() => redirectToDatahub()}>
                  <Nav.Link eventKey="redirecttocdh">
                    <Icon.ArrowUpRightSquareFill />
                    {isOpen ? <p>&nbsp;Cygnus Datahub&nbsp;</p> : ""}
                  </Nav.Link>
                </Nav.Item>
              }
            </Nav>
          </Tab.Container>
          {
            <Row className="sidenav-bottom-container">
              {isOpen && (
                <div className="sidenav-firm-icon">
                  {firm_img ? (
                    <img className="sidenav-firm-logo" src={firm_img} alt="fl" />
                  ) : (
                    <Icon.PersonWorkspace style={{ height: 25, width: 25 }} />
                  )}
                </div>
              )}

              {isOpen && (
                <div className="flex-row-space-center" style={{ padding: "0px 30px" }}>
                  <div className="sidenav-login-text">
                    <p className="login-caption">Logged&nbsp;in&nbsp;from</p>
                    <p className="login-firm">{firmname}</p>
                  </div>
                  <div
                    className="sidenav-logout-container"
                    onMouseEnter={sidenavOpenDoor}
                    onMouseLeave={sidenavCloseDoor}
                  >
                    {logoutIcon ? (
                      <span onClick={logout}><Icon.DoorOpenFill className="sidenav-logout" />&nbsp;Logout</span>
                    ) : (
                      <span onClick={logout}><Icon.DoorClosedFill className="sidenav-logout" />&nbsp;Logout</span>
                    )}
                  </div>
                </div>
              )}
            </Row>
          }
        </div>
        <div id="dashboard-content">{currentRender}</div>
      </div>
    </>
  );
};

export default Dashboard;
